import { DepthBufferReader } from "@faro-lotv/lotv";
import { Camera, Vector3, Vector3Tuple } from "three";

/** Resolution of the grid of visible points, enough to describe the data at a fraction of the cost of the full depth buffer */
export const VISIBLE_GRID_SIZE = 128;

/** The points of the point cloud visible on screen, sampled on a regular grid */
export type VisibleGrid = {
  width: number;
  height: number;
  /** Row major, null where no point is visible */
  points: Array<Vector3Tuple | null>;
};

const MM_DECIMALS = 3;

function round(value: number): number {
  return Number(value.toFixed(MM_DECIMALS));
}

/**
 * @param reader A reader holding the depth buffer of the current view
 * @param width Width of the depth buffer in pixels
 * @param height Height of the depth buffer in pixels
 * @param camera The camera that rendered the depth buffer
 * @returns the visible points on a regular grid over the screen
 */
export function sampleVisibleGrid(
  reader: DepthBufferReader,
  width: number,
  height: number,
  camera: Camera,
): VisibleGrid {
  const points: VisibleGrid["points"] = [];
  const point = new Vector3();
  for (let row = 0; row < VISIBLE_GRID_SIZE; row++) {
    const y = Math.floor(((row + 0.5) / VISIBLE_GRID_SIZE) * height);
    for (let column = 0; column < VISIBLE_GRID_SIZE; column++) {
      const x = Math.floor(((column + 0.5) / VISIBLE_GRID_SIZE) * width);
      if (reader.depthAt(x, y) < 1) {
        reader.worldCoordinatesAt(x, y, camera, point);
        points.push([round(point.x), round(point.y), round(point.z)]);
      } else {
        points.push(null);
      }
    }
  }
  return { width: VISIBLE_GRID_SIZE, height: VISIBLE_GRID_SIZE, points };
}

/**
 * @param grid The visible points on a grid
 * @returns the visible points
 */
export function listVisiblePoints(grid: VisibleGrid): Vector3Tuple[] {
  return grid.points.filter((point) => point !== null);
}
